import { Dialog, TextLink } from '@neo4j-ndl/react';
import { INSTANCE_STATUS, useInstances } from '@nx/state';
import type { Project } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { Suspense, lazy } from 'react';

import { StripeFormFallback } from '../loading-fallbacks';
import { MessageBanner } from '../project/billing/shared/modal';

const StripeForm = lazy(() => import('../project/billing/shared/stripe-form'));

interface Props {
  onClose: () => void;
  project: Project;
}

const PAUSED_STATUSES = [INSTANCE_STATUS.PAUSED, INSTANCE_STATUS.PAUSING];

export const ExtendTrialModal = ({ onClose, project }: Props) => {
  const { instances } = useInstances({ shouldPoll: false });
  const instance = instances.find((i) => isNotNullish(i.trialEndTime));
  const instancePaused = isNotNullish(instance) && PAUSED_STATUSES.includes(instance.instanceStatus);
  const shouldShowBanner = isNotNullish(instance) && !instancePaused;

  return (
    <Dialog isOpen onClose={onClose}>
      <Dialog.Header>Extend trial</Dialog.Header>
      <Dialog.Description>
        To extend your trial period, add a payment method. This will also remove trial limits and will provide access to{' '}
        <TextLink target="_blank" href="https://neo4j.com/pricing/">
          full Neo4j Aura features
        </TextLink>
        .
      </Dialog.Description>
      <Dialog.Content>
        {shouldShowBanner && <MessageBanner project={project} instance={instance} onClose={onClose} />}
        <Suspense fallback={<StripeFormFallback />}>
          <StripeForm
            submitButtonText="Extend trial"
            cancelButtonText="Cancel"
            isTrialExtension
            onCancel={onClose}
            onSuccess={onClose}
          />
        </Suspense>
      </Dialog.Content>
    </Dialog>
  );
};
